import React, { useState, useEffect } from 'react';
import axiosInstance from '../config/axios';
import logger from '../utils/logger';
import { Eye, EyeOff, Trash2, Download, CheckCircle, XCircle, Search, AlertTriangle, Save, RefreshCw } from 'lucide-react';
import './CountReview.css';
import { friendlyBatchItemLabel, batchItemTextSearchScore, compareBatchItemsByFriendlyDisplay, batchItemNotificationLabel } from '../utils/inventoryDisplay';
import {
  rowRequiresManagerAck,
  isValidAcknowledgementComment,
  flagTypeLabels,
  resolveEffectiveValueThreshold,
  buildThresholdContextForBatch,
  coerceReviewedCountQuantity,
  reviewedQtyOrZero
} from '../utils/countReviewFlags';
import {
  getFriendlyErrorMessage,
  getFriendlyError,
  EXPORT_FAILED_MESSAGE
} from '../utils/errorMessages';
import {
  isManagerBatchReviewable,
  isRefreshableReviewConflict,
  managerBatchStatusDisplay,
  refreshManagerDashboard
} from '../utils/managerCountStatus';

const parseDecimal = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const n = parseFloat(String(val).replace(',', '.'));
  return isNaN(n) ? null : n;
};

const roundTo2Decimals = (val) => {
  if (val === '' || val === null || val === undefined) return null;
  const n = parseFloat(String(val).replace(',', '.'));
  return isNaN(n) ? null : Math.round(n * 100) / 100;
};

/** Compare reviewed qty treating blank as 0 (matches save behaviour). */
const roundReviewedQty = (val) => roundTo2Decimals(reviewedQtyOrZero(val === undefined ? null : val));

const sanitizeDecimalInput = (value) => {
  if (value === '') return '';
  const withDots = String(value).replace(',', '.').replace(/[^0-9.]/g, '');
  const parts = withDots.split('.');
  if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('').substring(0, 2);
  if (parts.length === 2 && parts[1].length > 2) return parts[0] + '.' + parts[1].substring(0, 2);
  return withDots;
};

const formatDisplayQuantity = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const n = parseFloat(String(val).replace(',', '.'));
  return isNaN(n) ? '' : (Number.isInteger(n) ? String(n) : n.toFixed(2));
};

/** Payload qty: blank/null → 0; invalid → null (caller should not send). */
const reviewedQtyForPayload = (val) => {
  const c = coerceReviewedCountQuantity(val === undefined ? null : val);
  if (c.invalid) return null;
  return c.skip ? 0 : c.value;
};

/** Same as site My Counts: API may expose category or pivotCategory */
const batchItemCategory = (item) => {
  const raw = item?.pivotCategory ?? item?.category ?? '';
  const s = String(raw).trim();
  return s || '-';
};

/** Priority warning: line has a real count if any UOM quantity is non-zero (both when unit 2 applies). */
const priorityRowHasNonZeroCount = (item) => {
  const q1 = parseDecimal(item.countedQuantity);
  const q2 = parseDecimal(item.countedQuantity2);
  const n1 = q1 !== null ? q1 : 0;
  const n2 = q2 !== null ? q2 : 0;
  const hasUnit2 = item.unit2 && String(item.unit2).trim() !== '';
  if (hasUnit2) return n1 !== 0 || n2 !== 0;
  return n1 !== 0;
};

const CountReview = ({ batches, onRefresh, globalInventoryValueLimit, globalInventoryValueLimitsByCluster }) => {
  const thresholdContextForBatch = (batch) =>
    buildThresholdContextForBatch(batch, globalInventoryValueLimit, globalInventoryValueLimitsByCluster);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [status, setStatus] = useState('completed');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [reviewabilityConflict, setReviewabilityConflict] = useState(false);
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState({});
  const [expandSummaryByBatchId, setExpandSummaryByBatchId] = useState({});
  const [expandSummaryLoading, setExpandSummaryLoading] = useState({});
  const [expandSummaryError, setExpandSummaryError] = useState({});
  const [showPriorityWarning, setShowPriorityWarning] = useState(false);
  const [priorityWarningItems, setPriorityWarningItems] = useState([]);
  const [pendingBatchId, setPendingBatchId] = useState(null);
  const [transferBlockedSiteIds, setTransferBlockedSiteIds] = useState([]);
  const [showTransferBlockModal, setShowTransferBlockModal] = useState(false);
  const [transferBlockBatch, setTransferBlockBatch] = useState(null);
  const [editedCounts, setEditedCounts] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [locationFilters, setLocationFilters] = useState({});
  const [sortByBatch, setSortByBatch] = useState({});

  const [savingDraft, setSavingDraft] = useState(false);
  const [reviewDraftUpdatedAt, setReviewDraftUpdatedAt] = useState(null);
  const [resumedFromDraft, setResumedFromDraft] = useState(false);
  const [flagAckComments, setFlagAckComments] = useState({});
  const [savingFlagAcks, setSavingFlagAcks] = useState(false);

  const recoverFromReviewabilityError = async (err) => {
    if (!isRefreshableReviewConflict(err)) return false;
    setSelectedBatch(null);
    setShowPriorityWarning(false);
    setPendingBatchId(null);
    setNotes('');
    setEditedCounts({});
    setFlagAckComments({});
    setResumedFromDraft(false);
    setReviewDraftUpdatedAt(null);
    if (onRefresh) await onRefresh();
    setReviewabilityConflict(true);
    setError(getFriendlyErrorMessage(err, { kind: 'manager_review' }));
    return true;
  };

  const handleSort = (batchId, column) => {
    setSortByBatch(prev => {
      const current = prev[batchId] || { column: null, direction: 'asc' };
      const newDirection = current.column === column && current.direction === 'asc' ? 'desc' : 'asc';
      return { ...prev, [batchId]: { column, direction: newDirection } };
    });
  };

  const getFilteredAndSortedItems = (batch) => {
    const searchTerm = searchTerms[batch.batchId] || '';
    const locationFilter = locationFilters[batch.batchId] || '';
    const sortState = sortByBatch[batch.batchId] || { column: null, direction: 'asc' };
    let items = batch.items.filter(item => {
      if (locationFilter && !(item.location || '').toLowerCase().includes(locationFilter.toLowerCase())) return false;
      if (searchTerm.trim()) {
        const s = searchTerm.toLowerCase().trim();
        const desc = ((item.itemDescription || '') + '').toLowerCase();
        const vendorName = ((item.itemName || '') + '').toLowerCase();
        const catLower = ((item.pivotCategory || item.category || '') + '').toLowerCase();
        if (!desc.includes(s) && !vendorName.includes(s) && !catLower.includes(s) &&
            !(item.packSize || '').toLowerCase().includes(s) &&
            !(item.location || '').toLowerCase().includes(s) && !(item.notes || '').toLowerCase().includes(s) &&
            !(item.unit1 || '').toLowerCase().includes(s) && !(item.unit2 || '').toLowerCase().includes(s)) return false;
      }
      return true;
    });
    if (searchTerm.trim() && !sortState.column) {
      const s = searchTerm.toLowerCase().trim();
      items = [...items].sort((a, b) => {
        const ra = batchItemTextSearchScore(a, s);
        const rb = batchItemTextSearchScore(b, s);
        if (rb !== ra) return rb - ra;
        return compareBatchItemsByFriendlyDisplay(a, b);
      });
    }
    if (sortState.column) {
      items = [...items].sort((a, b) => {
        let aVal, bVal;
        switch (sortState.column) {
          case 'item': {
            const cmp = compareBatchItemsByFriendlyDisplay(a, b);
            const dir = sortState.direction === 'asc' ? 1 : -1;
            return cmp * dir;
          }
          case 'category': aVal = (a.pivotCategory || '').toLowerCase(); bVal = (b.pivotCategory || '').toLowerCase(); break;
          case 'packSize': aVal = (a.packSize || '').toLowerCase(); bVal = (b.packSize || '').toLowerCase(); break;
          case 'unit': aVal = (a.unit1 || '').toLowerCase(); bVal = (b.unit1 || '').toLowerCase(); break;
          case 'counted': aVal = parseFloat(a.countedQuantity) ?? -Infinity; bVal = parseFloat(b.countedQuantity) ?? -Infinity; break;
          case 'unit2': aVal = (a.unit2 || '').toLowerCase(); bVal = (b.unit2 || '').toLowerCase(); break;
          case 'counted2': aVal = parseFloat(a.countedQuantity2) ?? -Infinity; bVal = parseFloat(b.countedQuantity2) ?? -Infinity; break;
          case 'totalValue': aVal = parseFloat(a.itemValue) ?? 0; bVal = parseFloat(b.itemValue) ?? 0; break;
          case 'location': aVal = (a.location || '').toLowerCase(); bVal = (b.location || '').toLowerCase(); break;
          case 'priority': aVal = a.priority ? 1 : 0; bVal = b.priority ? 1 : 0; break;
          case 'notes': aVal = (a.notes || '').toLowerCase(); bVal = (b.notes || '').toLowerCase(); break;
          case 'alert': aVal = a.isHighValue ? 1 : 0; bVal = b.isHighValue ? 1 : 0; break;
          default: return 0;
        }
        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
        return sortState.direction === 'asc' ? cmp : -cmp;
      });
    }
    return items;
  };

  const SortHeader = ({ batchId, column, children }) => {
    const s = sortByBatch[batchId] || {};
    const isActive = s.column === column;
    return (
      <th className={'cr-sortable-th' + (isActive ? ' active' : '')} onClick={() => handleSort(batchId, column)}>
        {children} {isActive ? (s.direction === 'asc' ? '\u25B2' : '\u25BC') : ''}
      </th>
    );
  };

  const buildAckPayloadForBatch = (batch) => {
    const acknowledgements = [];
    for (const item of batch.items || []) {
      const { requiresManagerAck } = rowRequiresManagerAck(item, editedCounts, thresholdContextForBatch(batch));
      if (!requiresManagerAck) continue;
      const comment = flagAckComments[item.itemId] != null ? flagAckComments[item.itemId] : '';
      acknowledgements.push({ inventoryItemId: item.itemId, acknowledgementComment: comment });
    }
    return acknowledgements;
  };

  const countUnresolvedFlagAcks = (batch) => {
    if (!batch || !batch.items) return 0;
    let n = 0;
    const thresholdContext = thresholdContextForBatch(batch);
    for (const item of batch.items) {
      const { requiresManagerAck } = rowRequiresManagerAck(item, editedCounts, thresholdContext);
      if (!requiresManagerAck) continue;
      const comment = flagAckComments[item.itemId] != null ? flagAckComments[item.itemId] : '';
      if (!isValidAcknowledgementComment(comment)) n += 1;
    }
    return n;
  };

  const getUnresolvedFlagAckItems = (batch) => {
    if (!batch || !batch.items) return [];
    const thresholdContext = thresholdContextForBatch(batch);
    const unresolved = [];
    for (const item of batch.items) {
      const { requiresManagerAck } = rowRequiresManagerAck(item, editedCounts, thresholdContext);
      if (!requiresManagerAck) continue;
      const comment = flagAckComments[item.itemId] != null ? flagAckComments[item.itemId] : '';
      if (!isValidAcknowledgementComment(comment)) unresolved.push(item);
    }
    return unresolved;
  };

  const focusFirstMissingAcknowledgement = (batch) => {
    const first = getUnresolvedFlagAckItems(batch)[0];
    if (!first) return;
    const el = document.getElementById(`ack-${batch.batchId}-${first.itemId}`);
    if (el) {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      } catch (_) {
        /* ignore */
      }
    }
  };

  const countHiddenUnresolvedFlagAcks = (batch) => {
    const visibleIds = new Set(getFilteredAndSortedItems(batch).map((i) => i.itemId));
    return getUnresolvedFlagAckItems(batch).filter((item) => !visibleIds.has(item.itemId)).length;
  };

  const BATCH_ITEM_COL_COUNT = 11;

  const handleSaveFlagAcknowledgements = async (batchId) => {
    const batch = batches.find((b) => b.batchId === batchId);
    if (!batch) return;
    setError('');
    setReviewabilityConflict(false);
    setSuccess('');
    setSavingFlagAcks(true);
    try {
      const edits = [];
      const newCounts = [];
      for (const itemId of Object.keys(editedCounts)) {
        const ec = editedCounts[itemId];
        if (!ec.existingCountId) {
          const q1 = reviewedQtyForPayload(ec.countedQuantity);
          const q2 = reviewedQtyForPayload(ec.countedQuantity2);
          if ((q1 !== null && q1 !== 0) || (q2 !== null && q2 !== 0)) {
            newCounts.push({ inventoryItemId: ec.itemId, countedQuantity: q1 ?? 0, countedQuantity2: q2 ?? 0 });
          }
          continue;
        }
        const r1 = roundReviewedQty(ec.countedQuantity);
        const r1o = roundTo2Decimals(ec.originalQuantity);
        const r2 = roundReviewedQty(ec.countedQuantity2);
        const r2o = roundTo2Decimals(ec.originalQuantity2);
        if (r1 !== r1o || r2 !== r2o) {
          const edit = { countId: ec.existingCountId };
          if (r1 !== r1o) edit.countedQuantity = reviewedQtyForPayload(ec.countedQuantity);
          if (r2 !== r2o) edit.countedQuantity2 = reviewedQtyForPayload(ec.countedQuantity2);
          edits.push(edit);
        }
      }
      const res = await axiosInstance.put(`/api/tenant/counts/batch/${batchId}/flag-acknowledgements`, {
        acknowledgements: buildAckPayloadForBatch(batch),
        edits,
        newCounts
      });
      const saved = res.data?.acknowledgements || [];
      const next = { ...flagAckComments };
      for (const a of saved) {
        if (a.inventoryItemId) next[a.inventoryItemId] = a.acknowledgementComment || '';
      }
      setFlagAckComments(next);
      setSuccess('Flag acknowledgement comments saved');
    } catch (err) {
      if (!(await recoverFromReviewabilityError(err))) {
        setError(getFriendlyErrorMessage(err, { kind: 'manager_review' }));
      }
    } finally {
      setSavingFlagAcks(false);
    }
  };

  const handleBatchReview = async (batchId) => {
    setError(''); setReviewabilityConflict(false); setSuccess(''); setSubmitting(true);
    try {
      const batch = batches.find(b => b.batchId === batchId);
      if (!batch) { setError('Batch not found'); setSubmitting(false); return; }
      if (status === 'completed' && countUnresolvedFlagAcks(batch) > 0) {
        const friendly = getFriendlyError(
          { response: { status: 400, data: { code: 'MANAGER_ACK_REQUIRED' } } },
          { kind: 'manager_review' }
        );
        setError(`${friendly.title}: ${friendly.message}`);
        setSubmitting(false);
        focusFirstMissingAcknowledgement(batch);
        return;
      }
      const edits = [], newCounts = [];
      for (const itemId of Object.keys(editedCounts)) {
        const ec = editedCounts[itemId];
        if (!ec.existingCountId) {
          const q1 = reviewedQtyForPayload(ec.countedQuantity);
          const q2 = reviewedQtyForPayload(ec.countedQuantity2);
          if ((q1 !== null && q1 !== 0) || (q2 !== null && q2 !== 0)) {
            newCounts.push({ inventoryItemId: ec.itemId, countedQuantity: q1 ?? 0, countedQuantity2: q2 ?? 0 });
          }
          continue;
        }
        const r1 = roundReviewedQty(ec.countedQuantity);
        const r1o = roundTo2Decimals(ec.originalQuantity);
        const r2 = roundReviewedQty(ec.countedQuantity2);
        const r2o = roundTo2Decimals(ec.originalQuantity2);
        if (r1 !== r1o || r2 !== r2o) {
          const edit = { countId: ec.existingCountId };
          if (r1 !== r1o) edit.countedQuantity = reviewedQtyForPayload(ec.countedQuantity);
          if (r2 !== r2o) edit.countedQuantity2 = reviewedQtyForPayload(ec.countedQuantity2);
          edits.push(edit);
        }
      }
      logger.log('Updating batch status:', { batchId, status, notes, editCount: edits.length, newCountCount: newCounts.length });
      const acknowledgements = status === 'completed' ? buildAckPayloadForBatch(batch) : undefined;
      const response = await axiosInstance.put(`/api/tenant/counts/batch/${batchId}/status`, {
        status,
        notes,
        edits,
        newCounts,
        acknowledgements
      });
      logger.log('Batch status update response:', response.data);
      const countCount = response.data?.counts?.length || 0;
      const editMsg = (edits.length > 0 || newCounts.length > 0) ? ` (${edits.length} edited, ${newCounts.length} new)` : '';
      setSuccess(`Successfully ${status} count batch (${countCount} items)${editMsg}`);
      setSelectedBatch(null); setNotes(''); setEditedCounts({}); setResumedFromDraft(false); setReviewDraftUpdatedAt(null);
      if (onRefresh) setTimeout(() => onRefresh(), 800);
      else setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      console.error('Error updating batch status:', err);
      if (await recoverFromReviewabilityError(err)) return;
      const missing = err.response?.data?.missingAcknowledgements;
      const friendly = getFriendlyError(err, { kind: 'manager_review' });
      if (Array.isArray(missing) && missing.length) {
        setError(`${friendly.title}: ${friendly.message}`);
        const batch = batches.find((b) => b.batchId === batchId);
        if (batch) focusFirstMissingAcknowledgement(batch);
      } else {
        setError(friendly.message || 'Failed to update count status');
      }
    } finally { setSubmitting(false); }
  };

  const handleExportToExcel = async () => {
    setError(''); setReviewabilityConflict(false); setSuccess(''); setExporting(true);
    try {
      const response = await axiosInstance.get('/api/tenant/counts/export-completed', { responseType: 'blob' });
      const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      let filename = `Inventory_Counts_Export_${new Date().toISOString().split('T')[0]}.zip`;
      if (cd) { const m = cd.match(/filename="?(.+)"?/i); if (m) filename = m[1]; }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(`Successfully exported ${completedBatches.length} count batch(es) to ZIP file.`);
      setTimeout(() => window.location.reload(), 2000);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE);
    }
    finally { setExporting(false); }
  };

  const fetchPendingBatchCountSummary = async (batchId) => {
    setExpandSummaryLoading((prev) => ({ ...prev, [batchId]: true }));
    setExpandSummaryError((prev) => ({ ...prev, [batchId]: null }));
    try {
      const res = await axiosInstance.get(
        `/api/tenant/counts/batch/${encodeURIComponent(batchId)}/count-summary`
      );
      setExpandSummaryByBatchId((prev) => ({ ...prev, [batchId]: res.data }));
    } catch (err) {
      const msg = getFriendlyErrorMessage(err, {
        kind: 'manager_review',
        fallbackMessage: 'We could not load this count summary. Please refresh and try again.'
      });
      setExpandSummaryError((prev) => ({ ...prev, [batchId]: msg }));
    } finally {
      setExpandSummaryLoading((prev) => ({ ...prev, [batchId]: false }));
    }
  };

  const toggleExpandBatch = (batch) => {
    const batchId = batch.batchId;
    const isPending = batch.status === 'pending' || !batch.status;
    setExpandedBatches((prev) => {
      const opening = !prev[batchId];
      if (opening && isPending && selectedBatch !== batchId) {
        queueMicrotask(() => {
          void fetchPendingBatchCountSummary(batchId);
        });
      }
      return { ...prev, [batchId]: !prev[batchId] };
    });
  };

  const buildInitialEditedCounts = (batch) => {
    const initial = {};
    batch.items.forEach(item => {
      initial[item.itemId] = {
        countedQuantity: formatDisplayQuantity(item.countedQuantity ?? null),
        countedQuantity2: formatDisplayQuantity(item.countedQuantity2 ?? null),
        itemId: item.itemId, wasCounted: item.wasCounted, existingCountId: item.id,
        originalQuantity: item.countedQuantity ?? null, originalQuantity2: item.countedQuantity2 ?? null
      };
    });
    return initial;
  };

  const initEditedCountsForBatch = (batch) => {
    setEditedCounts(prev => {
      const merged = { ...prev };
      batch.items.forEach(item => {
        if (!merged[item.itemId]) {
          merged[item.itemId] = {
            countedQuantity: formatDisplayQuantity(item.countedQuantity ?? null),
            countedQuantity2: formatDisplayQuantity(item.countedQuantity2 ?? null),
            itemId: item.itemId, wasCounted: item.wasCounted, existingCountId: item.id,
            originalQuantity: item.countedQuantity ?? null, originalQuantity2: item.countedQuantity2 ?? null
          };
        }
      });
      return merged;
    });
  };

  const startReviewForBatch = async (batch) => {
    setError('');
    setReviewabilityConflict(false);
    setSuccess('');
    try {
      await axiosInstance.post(`/api/tenant/counts/batch/${batch.batchId}/review-start`);
      if (onRefresh) await onRefresh();
      return true;
    } catch (startErr) {
      console.warn('Mark review started:', startErr);
      if (!(await recoverFromReviewabilityError(startErr))) {
        setError(getFriendlyErrorMessage(startErr, { kind: 'manager_review' }));
      }
      return false;
    }
  };

  const openReviewForBatch = async (batch) => {
    setExpandedBatches(prev => ({ ...prev, [batch.batchId]: true }));
    setSelectedBatch(batch.batchId);
    setNotes('');
    setStatus('completed');
    setResumedFromDraft(false);
    setReviewDraftUpdatedAt(null);
    const initial = buildInitialEditedCounts(batch);
    try {
      const res = await axiosInstance.get(`/api/tenant/counts/batch/${batch.batchId}/review-draft`);
      const { draft, updatedAt } = res.data || {};
      if (draft && typeof draft === 'object') {
        if (draft.notes != null) setNotes(String(draft.notes));
        if (draft.status === 'rejected' || draft.status === 'completed') setStatus(draft.status);
        const validIds = new Set(batch.items.map(i => i.itemId));
        if (draft.editedCounts && typeof draft.editedCounts === 'object') {
          for (const itemId of Object.keys(draft.editedCounts)) {
            if (!validIds.has(itemId)) continue;
            const d = draft.editedCounts[itemId];
            const base = initial[itemId];
            if (!base) continue;
            initial[itemId] = {
              ...base,
              countedQuantity: d.countedQuantity !== undefined && d.countedQuantity !== null ? String(d.countedQuantity) : base.countedQuantity,
              countedQuantity2: d.countedQuantity2 !== undefined && d.countedQuantity2 !== null ? String(d.countedQuantity2) : base.countedQuantity2
            };
          }
        }
        setReviewDraftUpdatedAt(updatedAt || null);
        const hasEdits = draft.editedCounts && Object.keys(draft.editedCounts).length > 0;
        const hasNotes = draft.notes != null && String(draft.notes).trim() !== '';
        setResumedFromDraft(!!(hasEdits || hasNotes || !draft.reviewStarted));
      }
    } catch (err) {
      if (await recoverFromReviewabilityError(err)) return;
      console.warn('Load review draft:', err);
    }
    const initialAcks = {};
    for (const item of batch.items || []) {
      if (item.managerFlagAck && item.managerFlagAck.acknowledgementComment) {
        initialAcks[item.itemId] = item.managerFlagAck.acknowledgementComment;
      }
    }
    setFlagAckComments(initialAcks);
    setEditedCounts(initial);
  };

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/api/tenant/stock-transfers/pending-actions').then((res) => {
      if (!cancelled) setTransferBlockedSiteIds(res.data?.blockedSiteIds || []);
    }).catch(() => {
      if (!cancelled) setTransferBlockedSiteIds([]);
    });
    return () => { cancelled = true; };
  }, [batches]);

  const handleReviewCountClick = async (batch) => {
    if (batch.siteId && transferBlockedSiteIds.length > 0 && transferBlockedSiteIds.includes(batch.siteId)) {
      setTransferBlockBatch(batch);
      setShowTransferBlockModal(true);
      return;
    }
    if (!(await startReviewForBatch(batch))) return;
    const zeroItems = batch.items.filter(item => {
      const isPriority = item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1' || Boolean(item.priority);
      if (!isPriority) return false;
      if (!item.wasCounted) return true;
      return !priorityRowHasNonZeroCount(item);
    }).map(item => ({
      displayLabel: batchItemNotificationLabel(item),
      itemId: item.itemId,
      hasNoEntry: !item.wasCounted,
      countedQuantity: item.countedQuantity,
      countedQuantity2: item.countedQuantity2,
      unit2: item.unit2 || ''
    }));
    if (zeroItems.length > 0) { setPriorityWarningItems(zeroItems); setPendingBatchId(batch.batchId); setShowPriorityWarning(true); }
    else { openReviewForBatch(batch); }
  };

  const handleProceedWithReview = () => {
    setShowPriorityWarning(false);
    if (pendingBatchId) {
      const batch = batches.find(b => b.batchId === pendingBatchId);
      setPendingBatchId(null);
      if (batch) openReviewForBatch(batch);
    }
  };

  const handleSaveReviewDraft = async () => {
    if (!selectedBatch) return;
    setError('');
    setReviewabilityConflict(false);
    setSuccess('');
    setSavingDraft(true);
    try {
      const sanitized = {};
      for (const itemId of Object.keys(editedCounts)) {
        const ec = editedCounts[itemId];
        const o = {};
        if (ec.countedQuantity !== undefined && ec.countedQuantity !== null) o.countedQuantity = ec.countedQuantity;
        if (ec.countedQuantity2 !== undefined && ec.countedQuantity2 !== null) o.countedQuantity2 = ec.countedQuantity2;
        if (Object.keys(o).length) sanitized[itemId] = o;
      }
      const res = await axiosInstance.put(`/api/tenant/counts/batch/${selectedBatch}/review-draft`, {
        draft: { editedCounts: sanitized, status, notes }
      });
      const u = res.data?.updatedAt;
      setReviewDraftUpdatedAt(u || new Date().toISOString());
      setResumedFromDraft(true);
      setSuccess('Review draft saved');
      if (onRefresh) onRefresh();
    } catch (err) {
      if (!(await recoverFromReviewabilityError(err))) {
        setError(getFriendlyErrorMessage(err, { kind: 'manager_review' }));
      }
    } finally { setSavingDraft(false); }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm('Are you sure you want to delete this count batch? This action cannot be undone.')) return;
    setError(''); setReviewabilityConflict(false); setSuccess(''); setDeletingBatch(batchId);
    try {
      const response = await axiosInstance.delete(`/api/tenant/counts/batch/${batchId}`);
      setSuccess(`Successfully deleted count batch (${response.data.deletedCount} items)`);
      setTimeout(() => window.location.reload(), 1000);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, { kind: 'manager_review' }));
    }
    finally { setDeletingBatch(null); }
  };

  const getStatusBadge = (st) => {
    const display = managerBatchStatusDisplay(st);
    return (
      <span className={'cr-badge ' + display.cls} title={display.title}>
        {display.text}
      </span>
    );
  };

  /** Same rule as the card’s “X counted of Y total” (includes unsaved grid edits when present). */
  const lineCountsTowardHeadline = (item) => {
    const ec = editedCounts[item.itemId];
    if (ec) {
      const q1 = reviewedQtyOrZero(ec.countedQuantity !== undefined ? ec.countedQuantity : null);
      const q2 = reviewedQtyOrZero(ec.countedQuantity2 !== undefined ? ec.countedQuantity2 : null);
      // If only one field was edited blank and the other is undefined, fall back to original for unset fields
      const n1 = ec.countedQuantity !== undefined ? q1 : (parseDecimal(item.countedQuantity) ?? 0);
      const n2 = ec.countedQuantity2 !== undefined ? q2 : (parseDecimal(item.countedQuantity2) ?? 0);
      return n1 !== 0 || n2 !== 0;
    }
    return item.wasCounted;
  };

  const getEffectiveCountedCount = (batch) => {
    if (!batch.items) return 0;
    return batch.items.reduce((n, item) => n + (lineCountsTowardHeadline(item) ? 1 : 0), 0);
  };

  const pendingBatches = batches.filter(b => isManagerBatchReviewable(b.status));
  const completedBatches = batches.filter(b => b.status === 'completed');
  const otherBatches = batches.filter(b => b.status !== 'pending' && b.status !== 'completed' && b.status);

  /** Same line total as the Total Value column: (value1 × counted1) + (value2 × counted2). */
  const getLineTotalValue = (item) => {
    const ec = editedCounts[item.itemId] || null;
    const oq = item.countedQuantity ?? null;
    const oq2 = item.countedQuantity2 ?? null;
    const cq = ec ? ec.countedQuantity : formatDisplayQuantity(oq);
    const cq2 = ec ? (ec.countedQuantity2 !== undefined ? ec.countedQuantity2 : formatDisplayQuantity(oq2)) : formatDisplayQuantity(oq2);
    const v1 = parseFloat(String(item.value1 ?? '').replace(',', '.'));
    const v2 = parseFloat(String(item.value2 ?? '').replace(',', '.'));
    const q1 = parseDecimal(cq);
    const q2n = parseDecimal(cq2);
    const n1 = q1 !== null && q1 !== undefined ? q1 : 0;
    const n2 = q2n !== null && q2n !== undefined ? q2n : 0;
    const safeV1 = Number.isFinite(v1) ? v1 : 0;
    const safeV2 = Number.isFinite(v2) ? v2 : 0;
    return safeV1 * n1 + safeV2 * n2;
  };

  const formatLineTotalDisplay = (item, totalValue) => {
    if (!Number.isFinite(totalValue)) return '-';
    if (totalValue === 0 && !item.wasCounted) return '-';
    return totalValue.toFixed(2);
  };

  /** Sum of line totals (value1×qty1 + value2×qty2) only for lines that count toward the headline. */
  const getTotalCountedInventoryValueForBatch = (batch) => {
    if (!batch.items || batch.items.length === 0) return null;
    if (getEffectiveCountedCount(batch) === 0) return null;
    let sum = 0;
    for (const item of batch.items) {
      if (!lineCountsTowardHeadline(item)) continue;
      sum += getLineTotalValue(item);
    }
    return Number.isFinite(sum) ? sum : null;
  };

  const formatPendingSummaryTotalValue = (batch) => {
    const v = getTotalCountedInventoryValueForBatch(batch);
    return v == null ? '-' : v.toFixed(2);
  };

  const renderPendingBatchExpandSummary = (batch) => {
    if (!batch.items || batch.items.length === 0) {
      return (
        <div className="cr-pending-expand-summary" role="region" aria-label="Count summary">
          <dl className="cr-pending-expand-summary-grid">
            <div className="cr-pending-expand-summary-row">
              <dt>Lines counted</dt>
              <dd>-</dd>
            </div>
            <div className="cr-pending-expand-summary-row">
              <dt>Total count value</dt>
              <dd>-</dd>
            </div>
          </dl>
        </div>
      );
    }

    const batchId = batch.batchId;
    const totalLines = batch.totalItems || batch.items.length || 0;
    const hasQuantityEdits = batch.items.some((i) => editedCounts[i.itemId]);
    const api = expandSummaryByBatchId[batchId];
    const loading = expandSummaryLoading[batchId];
    const fetchErr = expandSummaryError[batchId];

    let countedDisplay;
    let totalLinesDisplay;
    let valueDisplay;

    if (hasQuantityEdits) {
      countedDisplay = getEffectiveCountedCount(batch);
      totalLinesDisplay = totalLines;
      valueDisplay = formatPendingSummaryTotalValue(batch);
    } else if (api && !loading) {
      countedDisplay = api.countedLines;
      totalLinesDisplay = api.totalLines;
      valueDisplay =
        api.countedLines === 0 || !Number.isFinite(api.totalCountValue) ? '-' : Number(api.totalCountValue).toFixed(2);
    } else {
      countedDisplay = getEffectiveCountedCount(batch);
      totalLinesDisplay = totalLines;
      valueDisplay = formatPendingSummaryTotalValue(batch);
    }

    return (
      <div className="cr-pending-expand-summary" role="region" aria-label="Count summary">
        {loading && <p className="cr-pending-expand-summary-loading">Updating summary…</p>}
        {fetchErr && <p className="cr-pending-expand-summary-error">{fetchErr}</p>}
        <dl className="cr-pending-expand-summary-grid">
          <div className="cr-pending-expand-summary-row">
            <dt>Lines counted</dt>
            <dd>{countedDisplay === '-' ? '-' : `${countedDisplay} of ${totalLinesDisplay}`}</dd>
          </div>
          <div className="cr-pending-expand-summary-row">
            <dt>Total count value</dt>
            <dd>{valueDisplay === '-' ? '-' : valueDisplay}</dd>
          </div>
        </dl>
      </div>
    );
  };

  const renderBatchItems = (batch) => {
    const items = getFilteredAndSortedItems(batch);
    const visibleTotalValue = items.reduce((sum, it) => sum + getLineTotalValue(it), 0);
    const locations = Array.from(new Set(batch.items.map(i => i.location).filter(l => l && l.trim()))).sort();
    return (
      <>
        <div className="cr-filters">
          <div className="cr-filter-group cr-filter-search">
            <Search size={14} className="cr-filter-icon" />
            <input type="text" className="cr-filter-input" placeholder="Search items..." value={searchTerms[batch.batchId] || ''} onChange={(e) => setSearchTerms(prev => ({ ...prev, [batch.batchId]: e.target.value }))} />
          </div>
          {locations.length > 0 && (
            <div className="cr-filter-group">
              <select className="cr-filter-select" value={locationFilters[batch.batchId] || ''} onChange={(e) => setLocationFilters(prev => ({ ...prev, [batch.batchId]: e.target.value }))}>
                <option value="">All Locations</option>
                {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="cr-table-wrap cr-table-wrap--batch-items">
          <table className="cr-table cr-table--batch-items">
            <thead>
              <tr>
                <SortHeader batchId={batch.batchId} column="item">Item</SortHeader>
                <SortHeader batchId={batch.batchId} column="packSize">Pack Size</SortHeader>
                <SortHeader batchId={batch.batchId} column="unit">Unit 1</SortHeader>
                <SortHeader batchId={batch.batchId} column="counted">Counted</SortHeader>
                <SortHeader batchId={batch.batchId} column="unit2">Unit 2</SortHeader>
                <SortHeader batchId={batch.batchId} column="counted2">Counted 2</SortHeader>
                <SortHeader batchId={batch.batchId} column="totalValue">Total Value</SortHeader>
                <SortHeader batchId={batch.batchId} column="location">Location</SortHeader>
                <SortHeader batchId={batch.batchId} column="priority">Priority</SortHeader>
                <SortHeader batchId={batch.batchId} column="notes">Notes</SortHeader>
                <SortHeader batchId={batch.batchId} column="alert">Alert</SortHeader>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const ec = editedCounts[item.itemId] || null;
                const oq = item.countedQuantity ?? null, oq2 = item.countedQuantity2 ?? null;
                const cq = ec ? ec.countedQuantity : formatDisplayQuantity(oq);
                const cq2 = ec ? (ec.countedQuantity2 !== undefined ? ec.countedQuantity2 : formatDisplayQuantity(oq2)) : formatDisplayQuantity(oq2);
                const isQ1Ed = ec && roundReviewedQty(ec.countedQuantity) !== roundTo2Decimals(oq);
                const isQ2Ed = ec && roundReviewedQty(ec.countedQuantity2) !== roundTo2Decimals(oq2);
                const isPriority = item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1' || Boolean(item.priority);
                const totalValue = getLineTotalValue(item);
                const batchThresholdContext = thresholdContextForBatch(batch);
                const { threshold: effectiveValueThreshold } = resolveEffectiveValueThreshold(item, batchThresholdContext);
                const overEffectiveValueLimit = effectiveValueThreshold != null && totalValue > effectiveValueThreshold;
                const { flagTypes, requiresManagerAck } = rowRequiresManagerAck(item, editedCounts, batchThresholdContext);
                const ackComment = flagAckComments[item.itemId] != null ? flagAckComments[item.itemId] : '';
                const ackMissing = requiresManagerAck && !isValidAcknowledgementComment(ackComment);
                const showAckField = selectedBatch === batch.batchId && requiresManagerAck;
                const flagLabels = flagTypeLabels(flagTypes);
                const rowClass =
                  'cr-table-row' +
                  (!item.wasCounted ? ' cr-row-uncounted' : '') +
                  (requiresManagerAck ? ' cr-row-flagged' : '') +
                  (ackMissing && selectedBatch === batch.batchId ? ' cr-row-flagged-missing' : '');
                return (
                  <React.Fragment key={item.id || item.itemId || index}>
                  <tr className={rowClass}>
                    <td className="cr-item-cell">
                      <div className="cr-item-name">{friendlyBatchItemLabel(item)}</div>
                      <div className="cr-item-category-row">
                        <span className="cr-category-pill">{batchItemCategory(item)}</span>
                      </div>
                    </td>
                    <td className="cr-pack-size-cell">{item.packSize || ''}</td>
                    <td>{item.unit1 ?? '-'}</td>
                    <td className="cr-td-num">
                      {selectedBatch === batch.batchId ? (
                        <div className="cr-edit-cell">
                          <input type="text" inputMode="decimal" className={'cr-edit-input' + (isQ1Ed ? ' edited' : '')} value={cq} onChange={(e) => {
                            const sanitized = sanitizeDecimalInput(e.target.value);
                            setEditedCounts(prev => ({ ...prev, [item.itemId]: { ...(prev[item.itemId] || {}), countedQuantity: sanitized, itemId: item.itemId, wasCounted: item.wasCounted, existingCountId: item.id, originalQuantity: (prev[item.itemId]?.originalQuantity !== undefined ? prev[item.itemId].originalQuantity : oq), originalQuantity2: oq2 } }));
                          }} onBlur={() => {
                            setEditedCounts(prev => {
                              const cur = prev[item.itemId];
                              if (!cur) return prev;
                              const raw = cur.countedQuantity;
                              if (raw !== '' && raw !== null && raw !== undefined) return prev;
                              return { ...prev, [item.itemId]: { ...cur, countedQuantity: '0' } };
                            });
                          }} placeholder="0" />
                          {isQ1Ed && <span className="cr-edit-flag" title={`Original: ${ec?.originalQuantity ?? '-'}`}>!</span>}
                        </div>
                      ) : formatDisplayQuantity(oq) || '-'}
                    </td>
                    <td>{item.unit2 || '-'}</td>
                    <td className="cr-td-num">
                      {selectedBatch === batch.batchId && item.unit2 ? (
                        <div className="cr-edit-cell">
                          <input type="text" inputMode="decimal" className={'cr-edit-input' + (isQ2Ed ? ' edited' : '')} value={cq2} onChange={(e) => {
                            const sanitized = sanitizeDecimalInput(e.target.value);
                            setEditedCounts(prev => ({ ...prev, [item.itemId]: { ...(prev[item.itemId] || {}), countedQuantity2: sanitized, itemId: item.itemId, wasCounted: item.wasCounted, existingCountId: item.id, originalQuantity: (prev[item.itemId]?.originalQuantity !== undefined ? prev[item.itemId].originalQuantity : oq), originalQuantity2: oq2 } }));
                          }} onBlur={() => {
                            setEditedCounts(prev => {
                              const cur = prev[item.itemId];
                              if (!cur) return prev;
                              const raw = cur.countedQuantity2;
                              if (raw !== '' && raw !== null && raw !== undefined) return prev;
                              return { ...prev, [item.itemId]: { ...cur, countedQuantity2: '0' } };
                            });
                          }} placeholder="0" />
                          {isQ2Ed && <span className="cr-edit-flag" title={`Original: ${ec?.originalQuantity2 ?? '-'}`}>!</span>}
                        </div>
                      ) : (item.unit2 ? (formatDisplayQuantity(oq2) || '-') : '-')}
                    </td>
                    <td className="cr-value-cell cr-td-num">
                        <span className="cr-value-amount">{formatLineTotalDisplay(item, totalValue)}</span>
                        {overEffectiveValueLimit && (
                          <button type="button" className="cr-value-over-limit-icon" data-tooltip="High Value Alert" title="High Value Alert" aria-label="High Value Alert">
                            <AlertTriangle size={18} />
                          </button>
                        )}
                      </td>
                    <td>{item.location || '-'}</td>
                    <td>{isPriority ? <span className="cr-priority-pill">Priority</span> : '-'}</td>
                    <td className="cr-notes-cell">{item.notes || '-'}</td>
                    <td>
                      {requiresManagerAck ? (
                        <div className="cr-flag-alert">
                          {flagLabels.map((label) => (
                            <span key={label} className="cr-alert-pill">{label}</span>
                          ))}
                        </div>
                      ) : item.isHighValue ? (
                        <span className="cr-alert-pill">High Value</span>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                  {showAckField && (
                    <tr className={'cr-ack-subrow' + (ackMissing ? ' cr-ack-subrow-missing' : '')}>
                      <td colSpan={BATCH_ITEM_COL_COUNT} className="cr-ack-subrow-cell">
                        <div className="cr-ack-field">
                          <label className="cr-ack-label" htmlFor={`ack-${batch.batchId}-${item.itemId}`}>
                            Manager acknowledgement required
                          </label>
                          {flagLabels.length > 0 && (
                            <p className="cr-ack-helper">
                              Required because this item triggered: {flagLabels.join(', ')}.
                            </p>
                          )}
                          <textarea
                            id={`ack-${batch.batchId}-${item.itemId}`}
                            className={'cr-ack-input' + (ackMissing ? ' cr-ack-input-missing' : '')}
                            rows={2}
                            placeholder="Enter acknowledgement comment for this flagged item…"
                            value={ackComment}
                            onChange={(e) =>
                              setFlagAckComments((prev) => ({ ...prev, [item.itemId]: e.target.value }))
                            }
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {items.length > 0 && (
            <div className="cr-batch-total" role="region" aria-label="Total count value for visible lines">
              <span className="cr-batch-total-label">Total count value</span>
              <span className="cr-batch-total-amount">{visibleTotalValue.toFixed(2)}</span>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="cr-section">
      {error && (
        <div className="cr-msg cr-msg-error cr-msg-with-action" role="alert">
          <span>{error}</span>
          {reviewabilityConflict && (
            <button type="button" className="btn btn-secondary cr-alert-refresh" onClick={refreshManagerDashboard}>
              <RefreshCw size={14} aria-hidden="true" /> Refresh
            </button>
          )}
        </div>
      )}
      {success && <div className="cr-msg cr-msg-success">{success}</div>}

      {showTransferBlockModal && (
        <div className="cr-modal-overlay">
          <div className="card cr-modal">
            <h3 className="cr-modal-title"><AlertTriangle size={18} /> Pending stock transfer</h3>
            <p className="cr-modal-desc">
              A pending stock transfer involves <strong>{transferBlockBatch?.siteName || 'this site'}</strong> and the other site in that request. Count review cannot continue for either site until the request is completed or rejected.
            </p>
            <p className="cr-modal-note">Open <strong>Transfers</strong> in the sidebar. The sending site manager must approve or reject; the recipient may cancel while it is still pending.</p>
            <div className="cr-modal-actions">
              <button type="button" className="btn cr-btn-primary" onClick={() => { setShowTransferBlockModal(false); setTransferBlockBatch(null); }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showPriorityWarning && priorityWarningItems.length > 0 && (
        <div className="cr-modal-overlay">
          <div className="card cr-modal">
            <h3 className="cr-modal-title"><AlertTriangle size={18} /> Priority Items Warning</h3>
            <p className="cr-modal-desc">The following priority items have been counted as <strong>zero (0)</strong> or have <strong>no entry</strong>:</p>
            <ul className="cr-modal-list">
              {priorityWarningItems.map((item, i) => (
                <li key={item.itemId || i} className="cr-modal-list-item">
                  {item.displayLabel}
                  <span className="cr-modal-list-detail">
                    {item.hasNoEntry
                      ? '(No entry)'
                      : item.unit2 && String(item.unit2).trim()
                        ? `(Count: ${formatDisplayQuantity(item.countedQuantity) || '0'} / ${formatDisplayQuantity(item.countedQuantity2) || '0'})`
                        : `(Count: ${formatDisplayQuantity(item.countedQuantity) || '0'})`}
                  </span>
                </li>
              ))}
            </ul>
            <p className="cr-modal-note">Please review these items carefully before completing the count review.</p>
            <div className="cr-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => { setShowPriorityWarning(false); setPriorityWarningItems([]); setPendingBatchId(null); }}>Cancel</button>
              <button type="button" className="btn cr-btn-danger" onClick={handleProceedWithReview}>Proceed with Review</button>
            </div>
          </div>
        </div>
      )}

      {completedBatches.length > 0 && (
        <div className="card cr-export-card">
          <div className="cr-export-header">
            <div>
              <h3 className="cr-export-title">Completed Counts Ready for Finance ({completedBatches.length})</h3>
              <p className="cr-export-desc">Export completed counts to a ZIP file containing an Excel spreadsheet.</p>
            </div>
            <button type="button" className="btn cr-btn-export" onClick={handleExportToExcel} disabled={exporting}>
              <Download size={15} /> {exporting ? 'Exporting...' : `Export ${completedBatches.length} Count(s) to ZIP`}
            </button>
          </div>
        </div>
      )}

      {pendingBatches.length > 0 && (
        <div className="cr-group">
          <div className="cr-group-header">
            <h3 className="cr-group-title">Pending Reviews ({pendingBatches.length})</h3>
            <p className="cr-group-subtitle">Review each count batch. Check all items, then reject or mark as completed.</p>
          </div>
          {pendingBatches.map(batch => (
            <div key={batch.batchId} className={'card cr-batch-card' + (selectedBatch === batch.batchId ? ' cr-batch-active' : '')}>
              <div className="cr-batch-header">
                <div className="cr-batch-info">
                  <strong className="cr-batch-site">{batch.siteName}</strong>
                  <span className="cr-batch-meta">({getEffectiveCountedCount(batch)} counted of {batch.totalItems || batch.items?.length || 0} total)</span>
                </div>
                <div className="cr-batch-actions">
                  {batch.hasManagerReviewDraft && <span className="cr-badge cr-badge-draft" title="Manager review in progress">In progress</span>}
                  {getStatusBadge(batch.status)}
                  <button
                    type="button"
                    className="cr-icon-btn"
                    title={expandedBatches[batch.batchId] ? 'Hide summary' : 'Show summary'}
                    onClick={() => toggleExpandBatch(batch)}
                  >
                    {expandedBatches[batch.batchId] ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  <button type="button" className="cr-icon-btn cr-icon-danger" title="Delete batch" onClick={() => handleDeleteBatch(batch.batchId)} disabled={deletingBatch === batch.batchId}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <div className="cr-batch-detail">
                <span>Counted by: {batch.personName || '-'}</span>
                <span>{batch.countDate ? new Date(batch.countDate).toLocaleString() : new Date(batch.createdAt).toLocaleString()}</span>
              </div>
              {batch.generalNotes && <div className="cr-batch-notes">Notes: {batch.generalNotes}</div>}

              {expandedBatches[batch.batchId] &&
                (selectedBatch === batch.batchId ? renderBatchItems(batch) : renderPendingBatchExpandSummary(batch))}

              {selectedBatch === batch.batchId ? (
                <div className="cr-review-form">
                  {status === 'completed' && countUnresolvedFlagAcks(batch) > 0 && (
                    <div className="cr-flag-ack-banner cr-msg-error" role="status">
                      {countUnresolvedFlagAcks(batch)} flagged line(s) still need a manager acknowledgement comment before you can complete this review.
                      {countHiddenUnresolvedFlagAcks(batch) > 0 && (
                        <span className="cr-flag-ack-filter-hint">
                          {' '}
                          {countHiddenUnresolvedFlagAcks(batch)} required acknowledgement(s) are hidden by your search or location filter — clear filters to see them.
                        </span>
                      )}
                    </div>
                  )}
                  {resumedFromDraft && (
                    <div className="cr-draft-banner" role="status">
                      Resumed saved review
                      {reviewDraftUpdatedAt && (
                        <span className="cr-draft-saved-at"> · Last saved {new Date(reviewDraftUpdatedAt).toLocaleString()}</span>
                      )}
                    </div>
                  )}
                  <div className="cr-review-field">
                    <label className="cr-review-label">Action</label>
                    <select className="cr-review-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="completed">Mark as Completed</option>
                      <option value="rejected">Reject</option>
                    </select>
                  </div>
                  <div className="cr-review-field">
                    <label className="cr-review-label">Review Notes (optional)</label>
                    <textarea className="cr-review-textarea" placeholder="Add any review notes..." value={notes} onChange={(e) => setNotes(e.target.value)} rows="3" />
                  </div>
                  <div className="cr-review-actions">
                    <button
                      type="button"
                      className="btn cr-btn-primary"
                      onClick={() => handleBatchReview(batch.batchId)}
                      disabled={submitting || (status === 'completed' && countUnresolvedFlagAcks(batch) > 0)}
                      title={
                        status === 'completed' && countUnresolvedFlagAcks(batch) > 0
                          ? 'Acknowledgement comments required for all flagged lines'
                          : undefined
                      }
                    >
                      <CheckCircle size={15} /> {submitting ? 'Processing...' : status === 'rejected' ? 'Reject Review' : 'Complete Review'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => handleSaveFlagAcknowledgements(batch.batchId)} disabled={submitting || savingFlagAcks}>
                      <Save size={15} /> {savingFlagAcks ? 'Saving...' : 'Save Acknowledgements'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={handleSaveReviewDraft} disabled={submitting || savingDraft}>
                      <Save size={15} /> {savingDraft ? 'Saving...' : 'Save Draft'}
                    </button>
                    <button type="button" className="btn btn-secondary" onClick={() => { setSelectedBatch(null); setNotes(''); setStatus('completed'); setEditedCounts({}); setFlagAckComments({}); setResumedFromDraft(false); setReviewDraftUpdatedAt(null); }} disabled={submitting}>
                      <XCircle size={15} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn cr-btn-review" onClick={() => handleReviewCountClick(batch)}>
                  <CheckCircle size={15} /> Review Count
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {otherBatches.length > 0 && (
        <div className="cr-group">
          <h3 className="cr-group-title">Other Counts ({otherBatches.length})</h3>
          <div className="card cr-other-card">
            <div className="cr-table-wrap">
              <table className="cr-table">
                <thead>
                  <tr>
                    <th>Site</th><th>Items</th><th>Counted By</th><th>Date & Time</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {otherBatches.map(batch => (
                    <tr key={batch.batchId} className="cr-table-row">
                      <td className="cr-item-name">{batch.siteName}</td>
                      <td>{batch.itemCount}</td>
                      <td>{batch.personName || '-'}</td>
                      <td className="cr-date-cell">{batch.countDate ? new Date(batch.countDate).toLocaleString() : new Date(batch.createdAt).toLocaleString()}</td>
                      <td>{getStatusBadge(batch.status)}</td>
                      <td>
                        {batch.status === 'recalled' && (
                          <button
                            type="button"
                            className="btn btn-secondary cr-row-refresh"
                            title="Refresh dashboard"
                            aria-label="Refresh dashboard"
                            onClick={refreshManagerDashboard}
                          >
                            <RefreshCw size={14} aria-hidden="true" /> Refresh
                          </button>
                        )}
                        {batch.status !== 'completed' && batch.status !== 'rejected' && (
                          <button type="button" className="cr-icon-btn cr-icon-danger" title="Delete" onClick={() => handleDeleteBatch(batch.batchId)} disabled={deletingBatch === batch.batchId}>
                            <Trash2 size={15} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {batches.length === 0 && <div className="card cr-empty">No counts to review.</div>}
    </div>
  );
};

export default CountReview;